export function formatDate(value: string | null): string {
  if (!value) return "\u2014";
  return new Date(value).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * A calendar day ("2026-09-01") as that same day.
 *
 * Not `formatDate`: `new Date("2026-09-01")` is UTC midnight, which is August
 * 31 anywhere west of Greenwich, so a date-only value would print a day early
 * -- a September milestone shown inside the closed month.
 */
export function calendarDay(value: string): string {
  const [year, month, day] = value.slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return value;
  return new Date(year, month - 1, day).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function formatDateTime(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatCurrency(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}

/**
 * One place for how numbers are written, so the same quantity does not appear
 * as 7963.3 on one screen and 7,963.30 on another.
 *
 * Every figure here is financial: thousands separators are not decoration,
 * they are what stops 1234567 being read as 123456.
 */

/** Dollars, whole -- cents are noise at the scale these screens report. */
export function money(n: number): string {
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

/** Dollars with cents, for per-hour rates where a dollar is a big share. */
export function rate(n: number): string {
  return `${n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}/hr`;
}

/**
 * Hours, always to two places and always grouped: 7,963.30, never 7963.3.
 * Fixed decimals keep a column of them aligned on the point.
 */
export function hours(n: number): string {
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** A percentage to one place. Null is "we cannot say", never 0%. */
export function percent(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  return `${n.toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
}

/**
 * "Sep 2025" from a period start date.
 *
 * Built from the date rather than taking NOS's PeriodName, which lives two
 * layers above the engine function that emits these rows. A caller holding a
 * date can render any label; nobody can recover a date from a GUID.
 *
 * UTC, not local: "2025-09-01" parsed in a timezone behind UTC lands on
 * 31 August and the bar is labelled with the wrong month.
 */
export function monthLabel(value: string | null): string {
  if (!value) return "undated";
  const parsed = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return "undated";
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * A figure the engine may omit for Aggregate users. Pages drop the whole
 * column for them; these are the belt to that braces, so an absent key that
 * slips through prints a dash -- never "$0", "NaN" or "undefined".
 */
export function optionalMoney(n: number | null | undefined): string {
  return n === null || n === undefined || !Number.isFinite(n) ? "\u2014" : money(n);
}

export function optionalHours(n: number | null | undefined): string {
  return n === null || n === undefined || !Number.isFinite(n) ? "\u2014" : hours(n);
}
